/**
 * ActiveGroupContext — the user's currently-selected "working group" for
 * create-side flows (new contract, import, new property).
 *
 * Loads `groups.list` via SWR (keyed on the token + the literal endpoint
 * name so other pages can invalidate it through `useInvalidate('groups.list')`
 * and the header chip updates automatically). Computes a sensible initial
 * pick (member-admin → member → first), and persists the choice in
 * localStorage keyed by tenant so a reload keeps you where you were.
 *
 * Pages stay dumb: they call `useActiveGroup()` and the SWR layer
 * threads the active group through the request header on every fetch.
 *
 * When the user only belongs to a single writeable group, the picker UI
 * is hidden — the active group is still surfaced in the top header so
 * the user always knows where their work is landing.
 *
 * Note: this provider can't use `useTenantQuery` because that hook reads
 * back from this context. We call `useSWR` directly; the cache key
 * matches the shape `useTenantQuery` produces (with a `null` group
 * segment for tenant-wide reads) so `useInvalidate('groups.list')`
 * matches both this provider's query and any in-page consumers.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import useSWR from 'swr';
import { setActiveGroupHeader, tenantApi, type GroupRow } from './api.js';
import { useTenantSession } from './auth-context.js';

type Ctx = {
  /**
   * Groups the actor can create in. The default-group flag does not gate
   * usability — every group the user is a member of is writeable.
   */
  writeableGroups: GroupRow[] | null;
  /** Full visible list. Kept for symmetry with `writeableGroups`. */
  allGroups: GroupRow[] | null;
  defaultGroupId: string | null;
  activeGroupId: string | null;
  setActiveGroupId: (groupId: string) => void;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const ActiveGroupCtx = createContext<Ctx>({
  writeableGroups: null,
  allGroups: null,
  defaultGroupId: null,
  activeGroupId: null,
  setActiveGroupId: () => undefined,
  loading: true,
  error: null,
  refresh: async () => undefined,
});

export function useActiveGroup(): Ctx {
  return useContext(ActiveGroupCtx);
}

const storageKey = (tenantId: string) => `regta.activeGroupId.${tenantId}`;

export function ActiveGroupProvider({ children }: { children: ReactNode }) {
  const { isReady, actor } = useTenantSession();
  const [activeGroupId, setActiveGroupIdState] = useState<string | null>(null);

  // Match the cache-key shape produced by `useTenantQuery` with
  // ignoreActiveGroup: [endpoint, userId, null]. This lets
  // `useInvalidate('groups.list')` revalidate this provider's data too.
  // We key on actor.id (Clerk user id) so the cache flips on sign-in/out
  // but never on token rotation.
  const userId = actor?.id ?? null;
  const { data, error, mutate } = useSWR(
    isReady && userId ? (['groups.list', userId, null] as const) : null,
    async () => tenantApi.groups.list(),
  );

  const allGroups: GroupRow[] | null = data?.groups ?? null;
  const defaultGroupId = data?.default_group_id ?? null;

  const writeableGroups = useMemo(() => {
    if (!allGroups) return null;
    return allGroups;
  }, [allGroups]);

  // Initial active-group resolution, gated on actor + loaded groups.
  useEffect(() => {
    if (!actor || !writeableGroups) return;
    if (activeGroupId) return;

    let pick: string | null = null;
    try {
      const stored = window.localStorage.getItem(storageKey(actor.tenantId));
      if (stored && writeableGroups.some((g) => g.id === stored)) {
        pick = stored;
      }
    } catch {
      // localStorage unavailable — fall through to heuristic.
    }

    if (!pick) {
      const adminMatch = writeableGroups.find((g) =>
        actor.memberships.some(
          (m) => m.groupId === g.id && m.role === 'admin',
        ),
      );
      const memberMatch =
        adminMatch ??
        writeableGroups.find((g) =>
          actor.memberships.some((m) => m.groupId === g.id),
        );
      pick = memberMatch?.id ?? writeableGroups[0]?.id ?? null;
    }

    setActiveGroupIdState(pick);
  }, [actor, writeableGroups, activeGroupId]);

  const setActiveGroupId = useCallback(
    (groupId: string) => {
      setActiveGroupIdState(groupId);
      if (actor) {
        try {
          window.localStorage.setItem(storageKey(actor.tenantId), groupId);
        } catch {
          // best-effort
        }
      }
    },
    [actor],
  );

  // Mirror the active group into the module-level header value consumed
  // by api.rpc(). Null on unmount / sign-out so a leftover header
  // doesn't ride along on the next session. Note: SWR queries set this
  // again per-fetch from their cache key, but this keeps non-SWR
  // call sites (e.g. one-off writes) using the right header too.
  useEffect(() => {
    setActiveGroupHeader(activeGroupId);
    return () => setActiveGroupHeader(null);
  }, [activeGroupId]);

  const refresh = useCallback(async () => {
    await mutate();
  }, [mutate]);

  return (
    <ActiveGroupCtx.Provider
      value={{
        writeableGroups,
        allGroups,
        defaultGroupId,
        activeGroupId,
        setActiveGroupId,
        loading: allGroups === null && !error,
        error: error ? (error as Error).message : null,
        refresh,
      }}
    >
      {children}
    </ActiveGroupCtx.Provider>
  );
}
